import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
  FC,
  MouseEvent,
  KeyboardEvent,
  ChangeEvent,
  startTransition,
} from 'react';
import { ClickAwayListener } from '@mui/material';
import Tooltip from 'shared/components/andtComponents/Tooltip';
import { GenerateIcon, ICONS } from '@pileus-cloud/anodot-frontend-common';
import { TagAutocompleteDropdownProps } from '../../types';
import { DropdownType } from '../../types';
import RenderEmptyState from './components/RenderEmptyState';
import RenderDropdownContent from './components/RenderDropdownContent';

import styles from './TagAutocompleteDropdown.module.scss';
import tooltipStyles from 'shared/components/andtComponents/Tooltip.module.scss';

const GovernanceTagAutocompleteDropdown: FC<TagAutocompleteDropdownProps> = ({
  label,
  options,
  value,
  onChange,
  onSelect,
  dropdownType,
  showCreateOption = false,
  onCreateButtonClick,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [locallyAddedOptions, setLocallyAddedOptions] = useState<string[]>([]);
  const [isCreatingMode, setIsCreatingMode] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const createInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleValueChange = useCallback(
    (newValue: string, isNewlyCreated: boolean = false) => {
      onChange(newValue);
      if (onSelect) {
        onSelect(newValue, isNewlyCreated);
      }
    },
    [onChange, onSelect],
  );

  const localOptions = useMemo(() => {
    const uniqueLocallyAdded = locallyAddedOptions.filter((option) => !options.includes(option));

    if (uniqueLocallyAdded.length > 0) {
      return [...uniqueLocallyAdded, ...options];
    }

    return options;
  }, [options, locallyAddedOptions]);

  const handleValueTypeOptions = useCallback(() => {
    if (dropdownType === DropdownType.VALUE && Array.isArray(value)) {
      const newOptionsToAdd: string[] = [];

      value.forEach((selectedValue) => {
        if (selectedValue && !localOptions.includes(selectedValue) && !locallyAddedOptions.includes(selectedValue)) {
          newOptionsToAdd.push(selectedValue);
        }
      });

      if (newOptionsToAdd.length > 0) {
        setLocallyAddedOptions((prev) => [...newOptionsToAdd, ...prev]);
      }
    }
  }, [dropdownType, value, localOptions, locallyAddedOptions]);

  const handleKeyTypeOptions = useCallback(() => {
    const isKeyDropdown = dropdownType === DropdownType.KEY;
    const isStringValue = typeof value === 'string';
    const hasValue = Boolean(value);
    const isValueNotInOptions = !localOptions.includes(value as string);
    const isValueNotInLocallyAdded = !locallyAddedOptions.includes(value as string);

    if (isKeyDropdown && isStringValue && hasValue && isValueNotInOptions && isValueNotInLocallyAdded) {
      setLocallyAddedOptions((prev) => [value as string, ...prev]);
    }
  }, [dropdownType, value, localOptions, locallyAddedOptions]);

  useEffect(() => {
    if (dropdownType === DropdownType.VALUE && Array.isArray(value)) {
      handleValueTypeOptions();
    } else {
      handleKeyTypeOptions();
    }
  }, [value, dropdownType, localOptions, locallyAddedOptions, handleValueTypeOptions, handleKeyTypeOptions]);

  const filteredOptions = useMemo(() => {
    if (searchValue) {
      const validOptions = localOptions.filter(
        (option): option is string => typeof option === 'string' && option !== null && option !== undefined,
      );

      return validOptions.filter((option) => option.toLowerCase().includes(searchValue.toLowerCase()));
    } else {
      return localOptions;
    }
  }, [searchValue, localOptions]);

  useEffect(() => {
    if (isCreatingMode && createInputRef.current) {
      createInputRef.current.focus();
    } else if (isOpen && dropdownRef.current && !isCreatingMode) {
      const searchInput = dropdownRef.current.querySelector('input');
      if (searchInput) {
        searchInput.focus();
      }
    }
  }, [isOpen, isCreatingMode]);

  const handleInputClick = useCallback(
    (e: MouseEvent) => {
      if (disabled) return;
      e.stopPropagation();
      setIsOpen(true);
      if (dropdownType === DropdownType.KEY && value && typeof value === 'string') {
        setSearchValue(value);
      }
    },
    [dropdownType, value, disabled],
  );

  const handleClickAway = useCallback(() => {
    if (disabled) return;
    if (isOpen) {
      if (dropdownType === DropdownType.KEY && searchValue && !isCreatingMode) {
        handleValueChange(searchValue);
      }

      startTransition(() => {
        setIsOpen(false);
        setSearchValue('');
        setIsCreatingMode(false);
      });
    }
  }, [isOpen, dropdownType, searchValue, isCreatingMode, handleValueChange, disabled]);

  const handleOptionSelect = useCallback(
    (option: string) => {
      if (dropdownType === DropdownType.KEY) {
        handleValueChange(option);
      } else {
        handleValueChange(option);
      }

      setIsOpen(false);
      setSearchValue('');
    },
    [dropdownType, handleValueChange],
  );

  const handleSearchChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (disabled) return;
      setSearchValue(e.target.value);
    },
    [disabled],
  );

  const handleCreateNew = useCallback(() => {
    if (disabled) return;

    startTransition(() => {
      setIsCreatingMode(true);
      setSearchValue('');
    });

    if (onCreateButtonClick) {
      onCreateButtonClick();
    }
  }, [onCreateButtonClick, disabled]);

  const confirmNewValue = useCallback(() => {
    if (searchValue) {
      const newValue = searchValue;

      setLocallyAddedOptions((prev) => {
        if (!prev.includes(newValue)) {
          return [newValue, ...prev];
        }
        return prev;
      });

      if (dropdownType === DropdownType.KEY) {
        handleValueChange(newValue, true);

        startTransition(() => {
          setSearchValue('');
          setIsCreatingMode(false);
          setIsOpen(false);
        });
      } else {
        handleValueChange(newValue, true);

        startTransition(() => {
          setSearchValue('');
          setIsCreatingMode(false);
          setIsOpen(false);
        });
      }
    }
  }, [searchValue, dropdownType, handleValueChange]);

  const cancelNewValue = useCallback(() => {
    startTransition(() => {
      setSearchValue('');
      setIsCreatingMode(false);
      if (dropdownType === DropdownType.KEY) {
        setIsOpen(false);
      }
    });
  }, [dropdownType]);

  const handleRadioChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>, option: string) => {
      e.stopPropagation();
      handleOptionSelect(option);
    },
    [handleOptionSelect],
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (disabled) return;
      if (e.key === 'Enter') {
        if (searchValue && (isCreatingMode || filteredOptions.length === 0)) {
          confirmNewValue();
        } else if (filteredOptions.length > 0) {
          handleOptionSelect(filteredOptions[0]);
        } else if (dropdownType === DropdownType.KEY && searchValue) {
          handleValueChange(searchValue);
          startTransition(() => {
            setIsOpen(false);
            setSearchValue('');
          });
        }
      } else if (e.key === 'Escape') {
        if (isCreatingMode) {
          startTransition(() => {
            setIsCreatingMode(false);
            setSearchValue('');
          });
        } else {
          startTransition(() => {
            setIsOpen(false);
            setSearchValue('');
          });
        }
      }
    },
    [
      disabled,
      isCreatingMode,
      searchValue,
      confirmNewValue,
      filteredOptions,
      handleOptionSelect,
      dropdownType,
      handleValueChange,
    ],
  );

  const isOptionSelected = useCallback(
    (option: string): boolean => {
      if (dropdownType === DropdownType.KEY) {
        return value === option;
      } else {
        return value === option;
      }
    },
    [value, dropdownType],
  );

  const getDisplayValue = useMemo((): string => {
    if (dropdownType === DropdownType.KEY) {
      return isOpen ? searchValue : (value as string) || '';
    } else {
      return value || searchValue || '';
    }
  }, [searchValue, value, dropdownType, isOpen]);

  const shouldShowPlaceholder = useMemo(() => {
    return !getDisplayValue;
  }, [getDisplayValue]);

  const renderEmptyState = useCallback(() => {
    return (
      <RenderEmptyState
        dropdownType={dropdownType}
        showCreateOption={showCreateOption}
        searchValue={searchValue}
        handleSearchChange={handleSearchChange}
        handleKeyDown={handleKeyDown}
        confirmNewValue={confirmNewValue}
        cancelNewValue={cancelNewValue}
      />
    );
  }, [dropdownType, showCreateOption, searchValue, handleSearchChange, handleKeyDown, confirmNewValue, cancelNewValue]);

  const DropdownContent = useMemo(() => {
    const content = (
      <div ref={containerRef} className={styles.dropdownContainer}>
        <ClickAwayListener onClickAway={handleClickAway} mouseEvent="onMouseDown">
          <div>
            <div
              className={`${styles.inputWrapper} ${isOpen ? styles.active : ''} ${disabled ? styles.disabled : ''}`}
              onClick={handleInputClick}
            >
              <div className={styles.inputField}>
                {dropdownType === DropdownType.KEY ? (
                  <div className={styles.keyIconPrefix}>
                    <GenerateIcon iconName={ICONS.key.name} />
                  </div>
                ) : null}
                {dropdownType === DropdownType.VALUE ? <div className={styles.valuePrefix}>Value</div> : null}
                {shouldShowPlaceholder ? <span className={styles.placeholder}>{label}</span> : null}
                <input
                  ref={inputRef}
                  type="text"
                  className={`${styles.searchInput} ${dropdownType === DropdownType.KEY ? styles.withKeyIcon : ''} ${
                    dropdownType === DropdownType.VALUE ? styles.withValuePrefix : ''
                  }`}
                  value={getDisplayValue}
                  onChange={handleSearchChange}
                  onKeyDown={handleKeyDown}
                  readOnly={true}
                  disabled={disabled}
                />
                <div className={styles.dropdownArrow}></div>
              </div>
            </div>

            {isOpen && !disabled ? (
              <div ref={dropdownRef} className={styles.dropdown}>
                <RenderDropdownContent
                  isCreatingMode={isCreatingMode}
                  searchValue={searchValue}
                  handleSearchChange={handleSearchChange}
                  dropdownType={dropdownType}
                  handleKeyDown={handleKeyDown}
                  confirmNewValue={confirmNewValue}
                  cancelNewValue={cancelNewValue}
                  filteredOptions={filteredOptions}
                  localOptions={localOptions}
                  RenderEmptyState={renderEmptyState}
                  handleOptionSelect={handleOptionSelect}
                  isOptionSelected={isOptionSelected}
                  handleRadioChange={handleRadioChange}
                  showCreateOption={showCreateOption}
                  handleCreateNew={handleCreateNew}
                  createInputRef={createInputRef}
                />
              </div>
            ) : null}
          </div>
        </ClickAwayListener>
      </div>
    );

    if (disabled && dropdownType === DropdownType.VALUE) {
      return (
        <Tooltip
          title="Please select a key first"
          classes={{ tooltip: tooltipStyles.whiteTooltip, arrow: tooltipStyles.whiteArrow }}
          placement="top"
          arrow
        >
          {content}
        </Tooltip>
      );
    }

    return content;
  }, [
    isOpen,
    disabled,
    dropdownType,
    getDisplayValue,
    shouldShowPlaceholder,
    label,
    handleInputClick,
    handleSearchChange,
    handleKeyDown,
    handleClickAway,
    renderEmptyState,
    isCreatingMode,
    searchValue,
    confirmNewValue,
    cancelNewValue,
    filteredOptions,
    localOptions,
    handleOptionSelect,
    isOptionSelected,
    handleRadioChange,
    showCreateOption,
    handleCreateNew,
    createInputRef,
  ]);

  return DropdownContent;
};

export default GovernanceTagAutocompleteDropdown;
